import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ReactEDSWrapper from '../../utils/react-eds-wrapper';
import { Button, Pagination, Tag, Tooltip } from '@dsi/react-eds';
import { useTheme } from '../../hooks/useTheme';
import { niiService } from '../../services/niiService';
import { NiiRunType, NiiRunStatus, ReportSummary } from './types/nii.types';
import styles from './NiiLandingPage.module.css';

const AuditReleaseNodes: React.FC = () => {
  const { gplVersion } = useParams<{ gplVersion: string }>();
  const navigate = useNavigate();
  const { theme } = useTheme();

  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 6 });

  // Fetch completed reports for this GPL version
  const { data: reportsData, isLoading } = useQuery({
    queryKey: ['gpl-audit-release-nodes', gplVersion],
    queryFn: () =>
      niiService.getReports({
        type: NiiRunType.GPL_AUDIT,
        gplVersion: gplVersion || '',
        status: NiiRunStatus.COMPLETE
      }),
    enabled: !!gplVersion,
    refetchOnWindowFocus: false
  });

  const reports = reportsData?.data || [];

  // Deduplicate by nodeName — BFF returns sorted by created_at DESC, so first per node is latest
  const latestByNode = useMemo(() => {
    const nodeMap = new Map<string, ReportSummary>();
    for (const report of reports) {
      const name = report.nodeName || 'Unknown';
      if (!nodeMap.has(name)) {
        nodeMap.set(name, report);
      }
    }
    return Array.from(nodeMap.values());
  }, [reports]);

  // Pagination
  const totalPages = Math.ceil(latestByNode.length / pagination.pageSize);
  const paginatedNodes = latestByNode.slice(
    pagination.pageIndex * pagination.pageSize,
    (pagination.pageIndex + 1) * pagination.pageSize
  );

  const handleViewDetails = (report: ReportSummary) => {
    navigate(`/nii/audit-reports/release/${encodeURIComponent(gplVersion || '')}/${report.reportId}`, {
      state: { nodeName: report.nodeName, auditResult: report.auditResult, gplVersion: report.gplVersion }
    });
  };

  const renderAuditResult = (report: ReportSummary) => {
    if (!report.auditResult) return '—';
    const isCompliant = report.auditResult === 'compliant';
    return (
      <ReactEDSWrapper component={Tag} color={isCompliant ? 'green' : 'red'}>
        {isCompliant ? 'Compliant' : 'Non Compliant'}
      </ReactEDSWrapper>
    );
  };

  return (
    <div className={theme ? 'light-theme' : 'dark-theme'}>
      <div
        className={`${styles.settingsContainer} ${styles.settingsContainerPadding} ${theme ? styles.settingsContainerLight : styles.settingsContainerDark}`}
      >
        <div className={styles.settingsTitleContainer}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <h2 style={{ margin: 0 }}>
              {gplVersion} Nodes
            </h2>
            <ReactEDSWrapper component={Tooltip} message="Reports will be automatically archived after the configured retention period (3-6 months.)" pointer>
              <i className="icon icon-info" style={{ opacity: 0.6 }} />
            </ReactEDSWrapper>
            <span style={{ fontSize: '12px', color: '#878787', marginLeft: '8px' }}>
              {latestByNode.length > 0
                ? `${pagination.pageIndex * pagination.pageSize + 1} - ${Math.min((pagination.pageIndex + 1) * pagination.pageSize, latestByNode.length)} of ${latestByNode.length} items`
                : ''}
            </span>
          </div>

          {/* Loading */}
          {isLoading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '24px 0' }}>
              <div className="loader small" />
              <p>Loading nodes…</p>
            </div>
          )}

          {/* Empty state */}
          {!isLoading && latestByNode.length === 0 && (
            <div style={{ textAlign: 'center', padding: '48px 16px' }}>
              <i className="icon icon-info" style={{ fontSize: '24px', display: 'block', marginBottom: '8px' }} />
              <div style={{ fontWeight: 500 }}>No node reports found</div>
              <p style={{ color: '#878787', marginTop: '4px' }}>No completed reports for this release.</p>
            </div>
          )}

          {/* Table */}
          {!isLoading && latestByNode.length > 0 && (
            <>
              <div className="table">
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '12px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          Node Name <i className="icon icon-sorting" />
                        </div>
                      </th>
                      <th style={{ textAlign: 'left', padding: '12px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          Audit Result <i className="icon icon-sorting" />
                        </div>
                      </th>
                      <th style={{ textAlign: 'right', padding: '12px 16px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedNodes.map((report) => (
                      <tr
                        key={report.reportId}
                        style={{ transition: 'background-color 0.2s ease' }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = !theme ? '#404040' : '#DCDCDC';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '';
                        }}
                      >
                        <td style={{ padding: '12px 16px', borderTop: `1px solid ${!theme ? '#404040' : '#242424'}` }}>
                          {report.nodeName || 'Unknown'}
                        </td>
                        <td style={{ padding: '12px 16px', borderTop: `1px solid ${!theme ? '#404040' : '#242424'}` }}>
                          {renderAuditResult(report)}
                        </td>
                        <td style={{ padding: '12px 16px', borderTop: `1px solid ${!theme ? '#404040' : '#242424'}`, textAlign: 'right' }}>
                          <ReactEDSWrapper component={Button} onClick={() => handleViewDetails(report)}>
                            View Details
                          </ReactEDSWrapper>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 0 && (
                <ReactEDSWrapper
                  component={Pagination}
                  numPages={totalPages}
                  selectedPage={pagination.pageIndex + 1}
                  onPaginationChangePage={(page: number) => {
                    setPagination((prev) => ({ ...prev, pageIndex: page - 1 }));
                  }}
                  optionsList={[
                    { value: '6', label: '6', active: pagination.pageSize === 6 },
                    { value: '10', label: '10', active: pagination.pageSize === 10 },
                    { value: '20', label: '20', active: pagination.pageSize === 20 }
                  ]}
                  onPaginationChangeSelect={(size: string) => {
                    setPagination({ pageSize: Number(size), pageIndex: 0 });
                  }}
                  withActions
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditReleaseNodes;
